import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { DEFAULT_PRIMARY_COLOR } from 'app/core/theme/apply-primary-palette';
import { ThemePreferenceService } from 'app/core/services/theme-preference.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'primary-color-picker',
    templateUrl: './primary-color-picker.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
        :host {
            display: block;
            width: 100%;
        }

        .primary-color-row {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            min-height: 36px;
            padding: 0 8px;
            margin: 0;
            border-radius: 8px;
            cursor: pointer;
        }

        .primary-color-row:hover {
            background: rgba(15, 23, 42, 0.06);
        }

        .primary-color-swatch {
            position: relative;
            flex: 0 0 auto;
            width: 18px;
            height: 18px;
            overflow: hidden;
            border-radius: 9999px;
            border: 1px solid rgba(15, 23, 42, 0.18);
            box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
        }

        .primary-color-swatch input {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            padding: 0;
            border: 0;
            opacity: 0;
            cursor: pointer;
        }

        .primary-color-picker-label {
            flex: 1 1 auto;
            min-width: 0;
            font-size: 13px;
            font-weight: 500;
            color: var(--fuse-text-default, #1e293b);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .primary-color-reset {
            flex: 0 0 auto;
            padding: 0;
            border: 0;
            background: none;
            font: inherit;
            font-size: 12px;
            font-weight: 500;
            color: var(--fuse-text-secondary, #64748b);
            cursor: pointer;
        }

        .primary-color-reset:hover {
            color: var(--fuse-text-default, #1e293b);
        }

        .dark .primary-color-row:hover {
            background: rgba(248, 250, 252, 0.08);
        }

        .dark .primary-color-swatch {
            border-color: rgba(248, 250, 252, 0.28);
        }

        .dark .primary-color-picker-label {
            color: #f1f5f9;
        }

        .dark .primary-color-reset {
            color: #94a3b8;
        }

        .dark .primary-color-reset:hover {
            color: #f1f5f9;
        }
    `],
})
export class PrimaryColorPickerComponent implements OnInit, OnDestroy {
    primaryColor = DEFAULT_PRIMARY_COLOR;
    isCustomColor = false;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private _themePreferenceService: ThemePreferenceService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this._themePreferenceService.primaryColor$
            .pipe(takeUntil(this._destroy$))
            .subscribe((color) => {
                this.primaryColor = color;
                this.isCustomColor = this._themePreferenceService.isCustomColor;
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    onColorInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this._themePreferenceService.setPrimaryColor(value);
    }

    resetToDefault(): void {
        this._themePreferenceService.resetToDefault();
    }
}
